/**
 * t480u: the compact "Find a channel or person" box on the Chat heading line (desktop sidebar). It only holds the text
 * and the keys; Sidebar.tsx filters the list and moves the highlight. Esc or the × clears it. No global shortcut.
 */
import type { RefObject } from 'react';
import clsx from 'clsx';
import { X } from 'lucide-react';
import { SIDEBAR_FILTER_LABEL, SIDEBAR_FILTER_PLACEHOLDER } from './sidebarFilter';

interface Props {
  value: string;
  onChange: (v: string) => void;
  /** Up/Down and Enter while there's text. */
  onNavKey: (key: 'ArrowUp' | 'ArrowDown' | 'Enter') => void;
  activeId?: string;
  resultsId: string;
  inputRef: RefObject<HTMLInputElement>;
}

export function SidebarFilterBox({ value, onChange, onNavKey, activeId, resultsId, inputRef }: Props) {
  const typed = value.length > 0;
  return (
    <div className={clsx('flex min-w-0 flex-1 items-center rounded-md border bg-white/5 pl-2 pr-0.5 transition',
      'border-white/15 hover:border-white/30 focus-within:border-white/60')} data-testid="sidebar-filter">
      <input ref={inputRef} type="text" value={value} spellCheck={false} autoComplete="off"
        role="combobox" aria-expanded={typed} aria-controls={resultsId} aria-autocomplete="list" aria-activedescendant={typed ? activeId : undefined}
        aria-label={SIDEBAR_FILTER_LABEL} placeholder={SIDEBAR_FILTER_PLACEHOLDER} data-testid="sidebar-filter-input"
        onChange={e => onChange(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Escape') {
            if (!typed) return; // let Esc do its usual job (e.g. close the phone menu sheet)
            e.preventDefault(); e.stopPropagation(); onChange('');
          } else if (typed && (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter')) {
            if (e.nativeEvent.isComposing) return;
            e.preventDefault(); onNavKey(e.key);
          }
        }}
        className="h-7 min-w-0 flex-1 bg-transparent text-[12px] text-white outline-none placeholder:text-sidebar-muted" />
      {typed && (
        <button type="button" aria-label="Clear search" title="Clear (Esc)" data-testid="sidebar-filter-clear"
          onClick={() => { onChange(''); inputRef.current?.focus(); }} className="shrink-0 rounded p-1 text-sidebar-muted hover:text-white">
          <X className="h-3 w-3" aria-hidden />
        </button>
      )}
    </div>
  );
}
